import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { Button, makeStyles, Icons } from '~/lib/bn-material-ui';
import { useStateValue } from './data/order-calculator-context';
import { useSirenEntity } from '~/utility/BinSentry-ui-utility';
import BinLevelWeek from './bin-level-week';
import BinLevelLegend from './bin-level-legend';
import { useOrderForecast } from './data/order-forecast-helpers';

const useStyles = makeStyles((theme) => {
  const thisWeekButtonSize = '2rem';
  return {
    calendar: {
      display: 'flex',
      flexDirection: 'row',
      '& > span:first-of-type': {
        borderTopLeftRadius: '4px',
        borderBottomLeftRadius: '4px',
      },
      '& > span:last-of-type': {
        borderTopRightRadius: '4px',
        borderBottomRightRadius: '4px',
      },
    },
    nav: {
      background: 'white',
      border: `1px solid ${theme.palette.blue[50]}`,
      marginTop: '2.5rem',
      display: 'flex',
      '& > button': {
        padding: 0,
        borderRadius: 0,
      },
    },
    navButton: {
      minWidth: 'auto',
    },
    centerColumn: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    },
    thisWeekButton: {
      alignSelf: 'flex-end',
      width: thisWeekButtonSize,
      height: thisWeekButtonSize,
      margin: '0.25rem 0',
    },
  };
});

export default function BinLevels({ href, stats: binStatsEntity, fullTotal }) {
  const styles = useStyles();
  const [{
    activeOrder,
    activeOrderId,
    binLevelDateRange,
    binLevelData,
  }, dispatch] = useStateValue();

  const { start, end } = binLevelDateRange;

  const [dateRangeURL, setDateRangeURL] = useState(new URL(href));

  dateRangeURL.searchParams.set('startDate', start?.toUTC().toISO());
  dateRangeURL.searchParams.set('endDate', end?.toUTC().toISO());

  const { entity: binLevelsEntity } = useSirenEntity({ href: dateRangeURL });
  const { volumeMap: orderForecast } = useOrderForecast(binStatsEntity, activeOrderId, activeOrder.orderQuantity, activeOrder.deliveryDate);

  useEffect(() => setDateRangeURL(new URL(href)), [start, end, href, setDateRangeURL]);

  useEffect(() => {
    // Note that stats may not have properties if no bin measurements have occurred yet
    if (binLevelsEntity?.entities) {
      const binLevelData = buildBinLevelData({ binLevelsEntity, binStatsEntity, start, end });
      dispatch({ type: 'setBinLevelData', binLevelData });
    }
  }, [binLevelsEntity, binStatsEntity, dispatch, start, end]);

  if (!binLevelData) {
    return null;
  }

  const prev = () => dispatch({ type: 'moveBinLevelWindowBack' });
  const next = () => dispatch({ type: 'moveBinLevelWindowForward' });
  const today = () => dispatch({ type: 'setBinLevelTargetDate', binLevelTargetDate: DateTime.now() });

  return (
    <>
      <div className={styles.calendar}>
        <span className={styles.nav}>
          <Button className={styles.navButton} onClick={prev}>
            <Icons.NavigateBefore />
          </Button>
        </span>

        <div className={styles.centerColumn}>
          <Button
            variant='text'
            size='small'
            onClick={today}
            className={styles.thisWeekButton}
            endIcon={<Icons.Today/>}
          >
            today
          </Button>

          <BinLevelWeek fullTotal={fullTotal} data={binLevelData} orderForecast={orderForecast} />
        </div>

        <span className={styles.nav}>
          <Button className={styles.navButton} onClick={next}>
            <Icons.NavigateNext />
          </Button>
        </span>
      </div>

      <BinLevelLegend />
    </>
  );
}

function buildBinLevelData({ binLevelsEntity, binStatsEntity, start, end }) {
  if (!binLevelsEntity) {
    return;
  }

  const data = {};
  const { days } = end.diff(start, 'days');
  _.times(days, i => data[start.plus({ days: i }).toISODate()] = [] );

  // bins levels are ordered by quantity not date
  const history = _.chain(binLevelsEntity?.entities)
    .orderBy('properties.publishedAt')
    .groupBy(i => DateTime.fromISO(i.properties.publishedAt).toISODate())
    .value();

  const forecast = _.chain(binStatsEntity?.properties?.volume?.forecast)
    .orderBy('publishedAt')
    .groupBy(i => DateTime.fromISO(i.publishedAt).toISODate())
    .value();

  const mergeArrays = (a, b) => _.isArray(a) && a.concat(b);

  _.mergeWith(data, history, mergeArrays);
  _.mergeWith(data, forecast, mergeArrays);

  // fill in any missing readings
  for (const [key, values] of Object.entries(data)) {
    if (values.length === 6) {
      continue;
    }

    data[key] = _.times(6, i => {
      const val = _.find(values, (level) => {
        const hour = DateTime.fromISO(level.properties?.publishedAt || level.publishedAt).hour;
        return hour >= i * 4 && hour < ((i + 1) * 4);
      });
      return (val || { noData: true, low: 0 });
    });
  }

  return data;
}
